import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import {
  getBettieRecommendPictures,
  type GetBettieRecommendPicturesResponse
} from '../../services/bettieProfileService';

const BETTIE_RECOMMEND_PICTURES_NEXT_PAGE_STORAGE_KEY_PREFIX = 'bettieRecommendPictures:nextPage';

const getNextPageStorageKey = (chatRoomId: string, memberId?: string) => {
  return `${BETTIE_RECOMMEND_PICTURES_NEXT_PAGE_STORAGE_KEY_PREFIX}:${chatRoomId}:${memberId ?? ''}`;
};

const readNextPageFromSessionStorage = (chatRoomId: string, memberId?: string) => {
  try {
    const raw = sessionStorage.getItem(getNextPageStorageKey(chatRoomId, memberId));
    if (!raw) return null;
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < 1) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeNextPageToSessionStorage = (
  chatRoomId: string,
  memberId: string | undefined,
  page: number
) => {
  try {
    sessionStorage.setItem(getNextPageStorageKey(chatRoomId, memberId), String(page));
  } catch {
    // ignore
  }
};

export const useGetBettieRecommendPictures = (chatRoomId: string, memberId?: string) => {
  const initialPage = useMemo(() => {
    const nextPage = readNextPageFromSessionStorage(chatRoomId, memberId);
    // 없으면 1페이지
    return typeof nextPage === 'number' ? nextPage : 1;
  }, [chatRoomId, memberId]);

  const query = useInfiniteQuery<GetBettieRecommendPicturesResponse>(
    // initialPage를 key에 포함시켜, 재진입 시 sessionStorage 기반 다음 page로 자연스럽게 이어지도록 함
    ['bettieRecommendPictures', chatRoomId, memberId, initialPage],
    ({ pageParam = initialPage }) => getBettieRecommendPictures(chatRoomId, memberId, pageParam),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage?.meta?.pagination?.currentPage < lastPage?.meta?.pagination?.totalPages) {
          return lastPage?.meta?.pagination?.currentPage + 1;
        }
        return undefined;
      }
    }
  );

  const persistNextPageToLoad = (page: number) => {
    if (!chatRoomId) return;
    if (typeof page !== 'number' || Number.isNaN(page) || page < 1) return;
    writeNextPageToSessionStorage(chatRoomId, memberId, page);
  };

  return {
    ...query,
    initialPage,
    persistNextPageToLoad
  };
};
